/**
 * Browser implementation of `ScoreRepository`, backed by localStorage.
 *
 * This file exists so that `core/` never has to know storage is a thing. If
 * localStorage is unavailable (private mode, blocked cookies, a hostile
 * iframe), it degrades to an in-memory board for the session instead of
 * throwing.
 */

import {
  finalizeName,
  parseEntries,
  sortEntries,
  type ScoreEntry,
  type ScoreRepository,
} from '../core/scoreboard.ts';

const SCORES_KEY = 'kd-tetris.scores.v1';
const NAME_KEY = 'kd-tetris.player.v1';
const THEME_KEY = 'kd-tetris.theme.v1';
const AUDIO_KEY = 'kd-tetris.audio.v1';
const VOLUME_KEY = 'kd-tetris.volume.v1';
const MUSIC_KEY = 'kd-tetris.music.v1';

export class LocalScoreboard implements ScoreRepository {
  private memory: ScoreEntry[] | null = null;
  private memoryName: string | null = null;
  private memoryTheme: string | null = null;
  private memoryAudio: string | null = null;
  private memoryVolume: string | null = null;
  private memoryMusic: string | null = null;
  private readonly storage: Storage | null;

  constructor(storage: Storage | null = safeStorage()) {
    this.storage = storage;
  }

  /** True when scores survive a reload. */
  get persistent(): boolean {
    return this.storage !== null;
  }

  load(): ScoreEntry[] {
    if (!this.storage) return this.memory ? sortEntries(this.memory) : [];
    try {
      const raw = this.storage.getItem(SCORES_KEY);
      if (!raw) return [];
      return parseEntries(JSON.parse(raw));
    } catch {
      // Corrupt or unreadable payload: start from an empty board rather than
      // taking the whole app down.
      return [];
    }
  }

  save(entries: readonly ScoreEntry[]): void {
    const sorted = sortEntries(entries);
    if (!this.storage) {
      this.memory = sorted;
      return;
    }
    try {
      this.storage.setItem(SCORES_KEY, JSON.stringify(sorted));
    } catch {
      this.memory = sorted;
    }
  }

  loadName(): string | null {
    if (!this.storage) return this.memoryName;
    try {
      const raw = this.storage.getItem(NAME_KEY);
      return raw === null ? null : finalizeName(raw);
    } catch {
      return this.memoryName;
    }
  }

  saveName(name: string): void {
    this.memoryName = name;
    if (!this.storage) return;
    try {
      this.storage.setItem(NAME_KEY, name);
    } catch {
      // Keep the in-memory copy and move on.
    }
  }

  loadTheme(): string | null {
    if (!this.storage) return this.memoryTheme;
    try {
      return this.storage.getItem(THEME_KEY);
    } catch {
      return this.memoryTheme;
    }
  }

  saveTheme(theme: string): void {
    this.memoryTheme = theme;
    if (!this.storage) return;
    try {
      this.storage.setItem(THEME_KEY, theme);
    } catch {
      // Keep the in-memory copy and move on.
    }
  }

  loadAudio(): string | null {
    if (!this.storage) return this.memoryAudio;
    try {
      return this.storage.getItem(AUDIO_KEY);
    } catch {
      return this.memoryAudio;
    }
  }

  saveAudio(audio: string): void {
    this.memoryAudio = audio;
    if (!this.storage) return;
    try {
      this.storage.setItem(AUDIO_KEY, audio);
    } catch {
      // Keep the in-memory copy and move on.
    }
  }

  loadVolume(): string | null {
    if (!this.storage) return this.memoryVolume;
    try {
      return this.storage.getItem(VOLUME_KEY);
    } catch {
      return this.memoryVolume;
    }
  }

  saveVolume(volume: string): void {
    this.memoryVolume = volume;
    if (!this.storage) return;
    try {
      this.storage.setItem(VOLUME_KEY, volume);
    } catch {
      // Keep the in-memory copy and move on.
    }
  }

  loadMusic(): string | null {
    if (!this.storage) return this.memoryMusic;
    try {
      return this.storage.getItem(MUSIC_KEY);
    } catch {
      return this.memoryMusic;
    }
  }

  saveMusic(music: string): void {
    this.memoryMusic = music;
    if (!this.storage) return;
    try {
      this.storage.setItem(MUSIC_KEY, music);
    } catch {
      // Keep the in-memory copy and move on.
    }
  }

  clear(): void {
    this.memory = null;
    try {
      this.storage?.removeItem(SCORES_KEY);
    } catch {
      // Nothing to do.
    }
  }
}

/** localStorage can exist and still throw on access. Probe it once. */
function safeStorage(): Storage | null {
  try {
    const probe = '__kd_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}
